'use client'

import { useEffect, useMemo, useRef, useState } from 'react'

import { DEFAULT_VENUE_EXCHANGE } from '@/lib/configs/default-venue'
import { useAccountPrimaryExchange } from '@/service/account-exchanges.hook'
import { useUserAccounts } from '@/service/account.hook'
import { useInstrumentList } from '@/service/instrument.hook'
import { useMultiMarketDataStream } from '@/service/ws/use-market-data-stream'
import {
  Exchange,
  type Instrument,
  InstrumentKind,
  MarketDataType,
  type Ticker,
  type TickerList,
} from '@/types/generated/common/types'

// The majors first, in the order a newcomer recognizes them (same idea as the status bar).
const PRIORITY = [
  'BTC',
  'ETH',
  'SOL',
  'XRP',
  'DOGE',
  'HYPE',
  'BNB',
  'AVAX',
  'LINK',
  'ADA',
  'SUI',
  'TRX',
] as const
const MARKET_COUNT = 12
const FLUSH_MS = 1000

export interface HomeMarket {
  instrument: Instrument
  base: string
  ticker: Ticker | null
  /** Decimal places from the instrument's tick size. */
  priceDecimals: number
}

function decimalsFromTick(tickSize: string): number {
  const tick = Number(tickSize)
  if (!(tick > 0)) return 2
  return Math.min(8, Math.max(0, Math.ceil(-Math.log10(tick) - 1e-9)))
}

/** Keep fields a partial tick leaves out, so a row never blanks between full updates. */
function mergeTicker(previous: Ticker | undefined, next: Ticker): Ticker {
  if (!previous) return next
  const merged: Ticker = { ...previous }
  for (const [key, value] of Object.entries(next) as [keyof Ticker, Ticker[keyof Ticker]][]) {
    if (value !== undefined && value !== '')
      (merged as unknown as Record<string, unknown>)[key] = value
  }
  return merged
}

/**
 * The perps of the user's main venue (Deribit when no account is connected) for Home's market
 * overview: one per base asset, majors first, with live tickers flushed once a second — a
 * glanceable list doesn't need every WebSocket tick to re-render the page.
 */
export function useHomeMarkets(): {
  exchange: Exchange
  markets: HomeMarket[]
  isPending: boolean
} {
  const { data: accountsData, isPending: isAccountsLoading } = useUserAccounts()
  const firstVenue = accountsData?.accounts[0]?.tradeVenue
  const primary = useAccountPrimaryExchange(firstVenue)
  const exchange = primary !== Exchange.EXCHANGE_UNSPECIFIED ? primary : DEFAULT_VENUE_EXCHANGE
  const settled =
    !isAccountsLoading && (firstVenue === undefined || primary !== Exchange.EXCHANGE_UNSPECIFIED)
  const { data: instrumentData, isPending: isInstrumentsPending } = useInstrumentList(
    exchange,
    settled
  )

  const instruments = useMemo(() => {
    const perps = (instrumentData?.instruments ?? []).filter(
      (instrument) =>
        instrument.kind !== InstrumentKind.INSTRUMENT_KIND_OPTION &&
        instrument.symbol.toLowerCase().includes('perp')
    )
    const byBase = new Map<string, Instrument>()
    for (const instrument of perps) {
      const base = instrument.baseCurrency.toUpperCase()
      // Prefer the linear (USDC) contract when a venue lists both, like the status bar does.
      if (!byBase.has(base) || instrument.symbol.includes('USDC')) byBase.set(base, instrument)
    }
    const rank = (base: string) => {
      const index = (PRIORITY as readonly string[]).indexOf(base)
      return index === -1 ? PRIORITY.length : index
    }
    return [...byBase.entries()]
      .sort(([a], [b]) => rank(a) - rank(b))
      .slice(0, MARKET_COUNT)
      .map(([, instrument]) => instrument)
  }, [instrumentData])

  const payloads = useMemo(
    () =>
      instruments.map((instrument) => ({
        symbol: instrument.symbol,
        exchange: instrument.exchange || exchange,
        dataType: MarketDataType.MARKET_DATA_TYPE_TICKER,
      })),
    [instruments, exchange]
  )

  const pendingRef = useRef(new Map<string, Ticker>())
  const [tickers, setTickers] = useState<Map<string, Ticker>>(() => new Map())

  useMultiMarketDataStream({
    connected: payloads.length > 0,
    payloads,
    onMessage: (data: TickerList | undefined) => {
      for (const ticker of data?.tickers ?? []) {
        pendingRef.current.set(
          ticker.symbol,
          mergeTicker(pendingRef.current.get(ticker.symbol), ticker)
        )
      }
    },
  })

  useEffect(() => {
    const flush = () => {
      if (pendingRef.current.size === 0) return
      const pending = pendingRef.current
      pendingRef.current = new Map()
      setTickers((previous) => {
        const next = new Map(previous)
        for (const [symbol, ticker] of pending) {
          next.set(symbol, mergeTicker(previous.get(symbol), ticker))
        }
        return next
      })
    }
    const id = setInterval(flush, FLUSH_MS)
    return () => clearInterval(id)
  }, [])

  const markets = useMemo(
    () =>
      instruments.map((instrument) => ({
        instrument,
        base: instrument.baseCurrency.toUpperCase(),
        ticker: tickers.get(instrument.symbol) ?? null,
        priceDecimals: decimalsFromTick(instrument.tickSize || instrument.pricePrecision),
      })),
    [instruments, tickers]
  )

  return { exchange, markets, isPending: !settled || isInstrumentsPending }
}
